import routes from '@/routes/routes';
import { formatCents } from '@/utils/formatCents';
import { Popover } from 'antd';
import cn from 'classnames';
import Link from 'next/link';
import React, { ReactNode } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { MdCheck, MdClose } from 'react-icons/md';
import { getDisplayPriceAuctionCharityItem } from '../auctionCharity.service';
import { AuctionCharityItemDto, getFailureReasonDescription } from '../schedule/auctionCharity.dto';

type Props = {
  item: AuctionCharityItemDto;
  setActive?: (id: number) => void;
  isActive?: boolean;
  isCharityOwner?: boolean;
  isItemOwner?: boolean;
  onRemoveClick?: () => void;
  isChosenForRemoval?: boolean;
  isChangingStatus?: boolean;
  canEdit: boolean;
  isLink: boolean;
};

export const CharityAuctionShowcaseItemCard = ({
  item,
  setActive,
  isActive,
  isCharityOwner,
  isItemOwner,
  onRemoveClick,
  isChosenForRemoval,
  isChangingStatus,
  canEdit,
  isLink,
}: Props) => {
  const showcaseItem = item.showcaseItem;
  const image = showcaseItem.itemImages[0] || '/images/no_image.jpeg';
  const auctionInfo = showcaseItem.auctionInfo;
  const isGlobalLink = isLink && !canEdit;
  const isInnerLink = isLink && canEdit;

  const renderFailureReason = () => {
    return <>{getFailureReasonDescription(item.failureReason)}</>;
  };

  const Wrapper = isGlobalLink
    ? ({ children }: { children: ReactNode }) => (
        <Link href={routes.showcaseItem({ userId: showcaseItem.userId, id: showcaseItem.id })}>
          <a className="w-full">{children}</a>
        </Link>
      )
    : React.Fragment;

  const InnerWraper = isInnerLink
    ? ({ children }: { children: ReactNode }) => (
        <Link href={routes.showcaseItem({ userId: showcaseItem.userId, id: showcaseItem.id })}>
          <a className="w-full">{children}</a>
        </Link>
      )
    : React.Fragment;

  const renderPrice = () => {
    const cents = getDisplayPriceAuctionCharityItem(item);

    return <span className="text-primary">Highest Bid {formatCents(cents)}</span>;
  };

  return (
    <Wrapper>
      <div
        className={cn(
          'w-full rounded-lg flex flex-row items-center justify-between shadow-md h-[75px] text-base font-bold my-2 pr-6 bg-secondary-lighter gap-x-2',
          item.status === 'failed' && 'border-2 border-danger',
          isActive ? 'border-2 border-primary' : '',
          isLink ? 'hover:border-2 hover:border-primary' : '',
        )}
      >
        <InnerWraper>
          <div
            className="flex flex-row gap-x-3 items-center w-[90%]"
            onClick={() => setActive && setActive(item.id)}
          >
            <img
              className="w-[75px] h-[75px] rounded-l-lg"
              src={image}
              alt={`${showcaseItem.name} poster`}
            />
            <div className="flex flex-col gap-y-2 items-start text-start flex-1">
              {item.status === 'failed' ? (
                <Popover content={renderFailureReason()}>
                  <span className="text-white truncate max-w-[200px]">
                    {showcaseItem.name} <FaExclamationCircle className="inline-block text-danger" />
                  </span>
                </Popover>
              ) : (
                <span className="text-white truncate max-w-[200px]">{showcaseItem.name}</span>
              )}
              {renderPrice()}
            </div>
          </div>
        </InnerWraper>
        {isCharityOwner && canEdit && item.status !== 'confirmed' && (
          <div>
            <button
              className={cn(
                'btn btn-new p-2 border hover:bg-gray-300 hover:text-danger',

                isChosenForRemoval ? 'text-secondary bg-white' : 'text-white',
              )}
              onClick={onRemoveClick}
            >
              <MdClose />
            </button>
          </div>
        )}
        {(isItemOwner || isCharityOwner) && (
          <div>
            {item.status === 'confirmed' && (
              <MdCheck
                size={25}
                className={isChangingStatus ? 'text-gray-300' : 'text-success-bright'}
              />
            )}
            {item.status === 'denied' && (
              <MdClose size={25} className={isChangingStatus ? 'text-gray-300' : 'text-danger'} />
            )}
          </div>
        )}
      </div>
    </Wrapper>
  );
};
